import React, { MouseEventHandler, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useHistory } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import ReactTooltip from 'react-tooltip';
import { WarehouseProductCardProps } from './types';
import { formatIqd } from '../../../utils/money';
import {
  BadgeTone,
  StockStatus,
  PASTEL_BG_COLORS,
  StyledCardStatItem,
  StyledCardStatsRow,
  StyledCardThumbnail,
  StyledStatDot,
  StyledStatRow,
  StyledStatusBadge,
  StyledWarehouseCategoryNameLabel,
  StyledWarehouseProductCard,
  StyledWarehouseProductCardAction,
  StyledWarehouseProductCardDamagedUnitsWrapper,
  StyledWarehouseProductCardDateCreatedWrapper,
  StyledWarehouseProductCardImageWrapper,
  StyledWarehouseProductCardNameWrapper,
  StyledWarehouseProductCardOrderedWrapper,
  StyledWarehouseProductCardPriceWrapper,
  StyledWarehouseProductCardRemainingUnitsWrapper,
  StyledWarehouseProductCurrency,
  StyledWarehouseProductDateLabel,
  StyledWarehouseProductLabel,
  StyledWarehouseProductNameLabel,
  StyledWarehouseProductPrice,
} from './styles';
import Button from '../../atoms/Button';
import { ButtonMode } from '../../atoms/Button/enums';
import IconButton from '../../atoms/IconButton';
import { IconEdit, WarningIcon } from '../../../assets/svg/icons';
import Typography from '../../styles/Typography';
import ProductThumbnail from '../../atoms/ProductThumbnail';
import ProductMonogram from '../../atoms/ProductMonogram';
import routes from '../../../setup/consts/routes';
import { StyledBox } from '../../styles/Grid/styles';
import DamagedProductTransactions from '../DamagedProductTransactions';
import useToggle from '../../../hooks/useToggle';


const WarehouseProductCard: React.FC<WarehouseProductCardProps> = (props) => {
  // ** Props
  const {
    isHorizontal,
    images,
    coverImage,
    productName,
    categoryName,
    orderedQuantity,
    onHandQuantity,
    productId,
    createdOn,
    sku,
    priceIqd,
    damagedQuantity,
  } = props;

  // ** Hooks
  const history = useHistory();
  const intl = useIntl();
  const [
    damagedProductTransactionsModalIsOpen,
    { toggle: toggleDamagedProductTransactionsModal },
  ] = useToggle();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // ** Vars
  const allImages = useMemo(() => {
    const seen = new Set<string>();
    return [coverImage, ...(images ?? [])]
      .filter((img): img is string => Boolean(img))
      .filter((img) => { if (seen.has(img)) return false; seen.add(img); return true; });
  }, [coverImage, images]);

  const formattedDate = useMemo(() => {
    if (!createdOn) return '—';
    let d = new Date(createdOn);
    if (isNaN(d.getTime())) {
      const m = createdOn.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
      if (m) d = new Date(`${m[3]}-${m[2]}-${m[1]}`);
    }
    if (isNaN(d.getTime())) return createdOn;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }, [createdOn]);
  const formattedPrice = formatIqd(priceIqd);

  // ** Handlers
  const onEditButtonClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    history.push(generatePath(routes.EDIT_PRODUCT, { productId }));
  };

  const onCreateOrderButtonClick: MouseEventHandler = (e) => {
    e.stopPropagation();

    history.push(
      generatePath(routes.PLACE_PRODUCT_ORDER, {
        productId,
      })
    );
  };

  const onClickProductCard = () => {
    history.push(
      generatePath(routes.SHOW_PRODUCT, {
        id: productId,
      })
    );
  };

  const onDamagedButtonClick = (e: any) => {
    e.stopPropagation();
    toggleDamagedProductTransactionsModal();
  };

  // Red is only loud when there's a real signal:
  //   Remaining=0 is a problem ONLY if stock has actually moved (ordered>0)
  //   — otherwise it's a fresh/never-stocked product, not an out-of-stock alert.
  const orderedTone: 'good' | 'warn' | 'muted' =
    orderedQuantity > 0 ? 'good' : 'muted';
  const remainingTone: 'good' | 'warn' | 'muted' =
    onHandQuantity > 0
      ? 'good'
      : orderedQuantity > 0
      ? 'warn'
      : 'muted';
  const damagedTone: 'good' | 'warn' | 'muted' =
    damagedQuantity > 0 ? 'warn' : 'muted';

  // Derive stock status for badge
  const stockStatus: StockStatus =
    onHandQuantity === 0 && orderedQuantity > 0
      ? 'out'
      : onHandQuantity > 0 && onHandQuantity <= 5
      ? 'low'
      : 'in';

  // Pastel bg cycles through 6 colors based on productId
  const thumbnailBgColor = PASTEL_BG_COLORS[Math.abs(productId) % PASTEL_BG_COLORS.length];

  const badgeTone: BadgeTone =
    stockStatus === 'out' ? 'danger' : stockStatus === 'low' ? 'warning' : 'success';

  const badgeLabel =
    stockStatus === 'out'
      ? intl.formatMessage({ id: 'warehouse.outOfStock', defaultMessage: 'Out of stock' })
      : stockStatus === 'low'
      ? intl.formatMessage({ id: 'warehouse.lowStock', defaultMessage: 'Low stock' })
      : intl.formatMessage({ id: 'warehouse.inStock', defaultMessage: 'In stock' });

  const infoFields = (
    <>
      {/* Ordered */}
      <StyledWarehouseProductCardOrderedWrapper
        {...{ isHorizontal }}
        onClick={isHorizontal ? onClickProductCard : undefined}
      >
        {!isHorizontal && (
          <StyledWarehouseProductLabel>
            <FormattedMessage id='warehouse.ordered' defaultMessage='Ordered' />
          </StyledWarehouseProductLabel>
        )}
        <StyledStatRow>
          <StyledStatDot tone={orderedTone} />
          <Typography as='span' variant={isHorizontal ? 'body' : 'captionBold'}>
            {orderedQuantity.toLocaleString()}
          </Typography>
        </StyledStatRow>
      </StyledWarehouseProductCardOrderedWrapper>

      {/* Remaining */}
      <StyledWarehouseProductCardRemainingUnitsWrapper {...{ isHorizontal }}>
        {!isHorizontal && (
          <StyledWarehouseProductLabel>
            <FormattedMessage id='warehouse.remaining' defaultMessage='Remaining' />
          </StyledWarehouseProductLabel>
        )}
        <StyledStatRow>
          <StyledStatDot tone={remainingTone} />
          <Typography as='span' variant={isHorizontal ? 'body' : 'captionBold'}>
            {onHandQuantity.toLocaleString()}
          </Typography>
        </StyledStatRow>
      </StyledWarehouseProductCardRemainingUnitsWrapper>

      {/* Damaged */}
      <StyledWarehouseProductCardDamagedUnitsWrapper {...{ isHorizontal }}>
        {!isHorizontal && (
          <StyledWarehouseProductLabel>
            <FormattedMessage id='warehouse.damaged' defaultMessage='Damaged' />
          </StyledWarehouseProductLabel>
        )}
        <StyledBox gap='0.5rem' alignItems='center'>
          <StyledStatRow>
            <StyledStatDot tone={damagedTone} />
            <Typography as='span' variant={isHorizontal ? 'body' : 'captionBold'}>
              {damagedQuantity.toLocaleString()}
            </Typography>
          </StyledStatRow>
          {damagedQuantity ? (
            <IconButton
              style={{ width: '20px', height: '20px', border: 0 }}
              icon={WarningIcon}
              aria-label={intl.formatMessage({ id: 'warehouse.viewDamaged.ariaLabel', defaultMessage: 'View damaged transactions' })}
              onClick={onDamagedButtonClick}
            />
          ) : null}
        </StyledBox>
      </StyledWarehouseProductCardDamagedUnitsWrapper>

      {/* Created Date */}
      {isHorizontal ? (
        <StyledWarehouseProductCardDateCreatedWrapper {...{ isHorizontal }}>
          <Typography as='span' variant='body'>
            <span dir='ltr'>{formattedDate}</span>
          </Typography>
        </StyledWarehouseProductCardDateCreatedWrapper>
      ) : null}
    </>
  );

  return (
    <>
      <AnimatePresence>
        {damagedProductTransactionsModalIsOpen ? (
          <DamagedProductTransactions
            productId={productId}
            onClose={toggleDamagedProductTransactionsModal}
          />
        ) : null}
      </AnimatePresence>

      <StyledWarehouseProductCard {...{ isHorizontal }}>
        {/* ── Thumbnail area: only shown in grid (card) view ── */}
        {!isHorizontal && (
          <StyledCardThumbnail
            $bgColor={allImages.length ? undefined : thumbnailBgColor}
            onClick={onClickProductCard}
            style={{ cursor: 'pointer' }}
          >
            {allImages.length > 0 ? (
              <>
                <img
                  src={allImages[currentImageIndex]}
                  alt={productName}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#fff' }}
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      type='button'
                      aria-label='Previous image'
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((i) => (i - 1 + allImages.length) % allImages.length); }}
                      style={{
                        position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                        width: 26, height: 26, borderRadius: '50%', border: 'none',
                        background: 'rgba(0,0,0,0.35)', color: '#fff', cursor: 'pointer',
                        display: 'grid', placeItems: 'center', padding: 0, lineHeight: 1,
                        fontSize: 14, fontWeight: 700,
                      }}
                    >‹</button>
                    <button
                      type='button'
                      aria-label='Next image'
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((i) => (i + 1) % allImages.length); }}
                      style={{
                        position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                        width: 26, height: 26, borderRadius: '50%', border: 'none',
                        background: 'rgba(0,0,0,0.35)', color: '#fff', cursor: 'pointer',
                        display: 'grid', placeItems: 'center', padding: 0, lineHeight: 1,
                        fontSize: 14, fontWeight: 700,
                      }}
                    >›</button>
                    <div
                      style={{
                        position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                        display: 'flex', gap: 4,
                      }}
                    >
                      {allImages.map((_, idx) => (
                        <button
                          key={idx}
                          type='button'
                          aria-label={`Image ${idx + 1}`}
                          onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                          style={{
                            width: idx === currentImageIndex ? 16 : 6,
                            height: 6, borderRadius: 3, border: 'none', padding: 0,
                            background: idx === currentImageIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                            cursor: 'pointer', transition: 'width 0.2s ease, background 0.2s ease',
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <ProductMonogram name={productName} />
            )}
            <StyledStatusBadge tone={badgeTone}>{badgeLabel}</StyledStatusBadge>
          </StyledCardThumbnail>
        )}

        <StyledWarehouseProductCardImageWrapper {...{ isHorizontal }}>
          {isHorizontal ? (
            <ProductThumbnail
              src={coverImage}
              alt={productName}
              monogramName={productName}
            />
          ) : null}
        </StyledWarehouseProductCardImageWrapper>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}

        <StyledWarehouseProductCardNameWrapper
          {...{ isHorizontal }}
          onClick={onClickProductCard}
        >
          <StyledBox alignItems='center' justifyContent='space-between'>
            {/* Category Name (chip) */}
            <div data-tip={categoryName?.length > 35 ? categoryName : null}>
              <StyledWarehouseCategoryNameLabel isHorizontal={isHorizontal}>
                {categoryName || intl.formatMessage({ id: 'warehouse.uncategorized', defaultMessage: 'Uncategorized' })}
              </StyledWarehouseCategoryNameLabel>

              <ReactTooltip multiline />
            </div>

            {/* Created Date — dimmed, supplementary */}
            {!isHorizontal ? (
              <StyledWarehouseProductDateLabel>
                <span dir='ltr'>{formattedDate}</span>
              </StyledWarehouseProductDateLabel>
            ) : null}
          </StyledBox>

          {/* Sku */}
          <span style={{
            fontSize: 'var(--t-xs)',
            color: 'var(--muted)',
            fontFamily: 'var(--font-mono, monospace)',
            letterSpacing: '0.03em',
            display: 'block',
            marginTop: 2,
          }}>{sku}</span>

          {/* Product Name — bdi isolates mixed-script text from surrounding bidi context */}
          <div data-tip={productName?.length > 35 ? productName : null}>
            <StyledWarehouseProductNameLabel isHorizontal={isHorizontal}>
              <bdi>{productName || '-'}</bdi>
            </StyledWarehouseProductNameLabel>

            <ReactTooltip multiline />
          </div>
        </StyledWarehouseProductCardNameWrapper>

        {isHorizontal ? (
          infoFields
        ) : (
          <>
            {/* Compact metric grid for card view — matches spec 3-col grid */}
            <div
              style={{
                order: 2,
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8,
                padding: 10,
                background: 'var(--paper)',
                borderRadius: 'var(--r-md)',
                marginBottom: 'var(--s-3)',
                cursor: 'pointer',
              }}
              onClick={onClickProductCard}
            >
              {/* Ordered */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: 3, background: 'var(--brand-green)', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{intl.formatMessage({ id: 'warehouse.ordered', defaultMessage: 'Ordered' })}</span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 'var(--t-md)', fontVariantNumeric: 'tabular-nums' }}>{orderedQuantity.toLocaleString()}</div>
              </div>
              {/* Remaining */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: 3, background: 'var(--info)', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{intl.formatMessage({ id: 'warehouse.remaining', defaultMessage: 'Remaining' })}</span>
                </div>
                <div style={{ fontWeight: 700, fontSize: 'var(--t-md)', fontVariantNumeric: 'tabular-nums' }}>{onHandQuantity.toLocaleString()}</div>
              </div>
              {/* Damaged */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <span style={{ width: 5, height: 5, borderRadius: 3, background: damagedQuantity > 0 ? 'var(--danger)' : 'var(--muted-2)', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>{intl.formatMessage({ id: 'warehouse.damaged', defaultMessage: 'Damaged' })}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--t-md)', fontVariantNumeric: 'tabular-nums' }}>{damagedQuantity.toLocaleString()}</div>
                  {damagedQuantity > 0 && (
                    <IconButton
                      style={{ width: '16px', height: '16px', border: 0 }}
                      icon={WarningIcon}
                      aria-label={intl.formatMessage({ id: 'warehouse.viewDamaged.ariaLabel', defaultMessage: 'View damaged transactions' })}
                      onClick={onDamagedButtonClick}
                    />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {isHorizontal && (
          <StyledWarehouseProductCardPriceWrapper
            {...{ isHorizontal }}
            onClick={onClickProductCard}
          >
            <StyledWarehouseProductPrice onClick={onClickProductCard}>
              <Typography variant='body' as='span'>{formattedPrice}</Typography>
              <StyledWarehouseProductCurrency>IQD</StyledWarehouseProductCurrency>
            </StyledWarehouseProductPrice>
          </StyledWarehouseProductCardPriceWrapper>
        )}
        <StyledWarehouseProductCardAction
          {...{ isHorizontal }}
          onClick={onClickProductCard}
        >
          {/* Edit */}
          <IconButton
            icon={IconEdit}
            aria-label={intl.formatMessage({ id: 'warehouse.editProduct.ariaLabel', defaultMessage: 'Edit product' })}
            onClick={onEditButtonClick}
          />

          <Button
            mode={ButtonMode.Primary}
            onClick={onCreateOrderButtonClick}
          >
            <FormattedMessage
              id='warehouse.productCardButtonLabel'
              defaultMessage='Create order'
            />
          </Button>
        </StyledWarehouseProductCardAction>
      </StyledWarehouseProductCard>
    </>
  );
};

export default WarehouseProductCard;
